import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, User, Mail, Phone, Lock, Save, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { affiliateApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import AffiliateNav from "./AffiliateNav";

export default function AffiliateProfile() {
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthed, setUnauthed] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    affiliateApi.me()
      .then((a) => {
        setAffiliate(a);
        setName(a.name || "");
        setEmail(a.email || "");
        setPhone(a.phone || "");
      })
      .catch(() => setUnauthed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (unauthed) return <Navigate to="/affiliate/login" replace />;
  if (loading || !affiliate) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0E17]"><Loader2 className="h-7 w-7 text-amber-400 animate-spin" /></div>;
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updated = await affiliateApi.updateProfile({ name: name.trim(), email: email.trim().toLowerCase(), phone: phone.trim() });
      setAffiliate(updated);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setSavingPassword(true);
    try {
      await affiliateApi.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't change password.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E17] text-slate-200" data-testid="affiliate-profile-page">
      <AffiliateNav affiliateName={affiliate.name} />

      <div className="px-6 py-6 max-w-2xl mx-auto space-y-6">
        <form onSubmit={saveProfile} className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4" data-testid="affiliate-profile-form">
          <h3 className="text-[14px] font-bold text-slate-100">Your details</h3>

          <Field icon={User} label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="affiliate-profile-name" className={inputCls} />
          </Field>
          <Field icon={Mail} label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="affiliate-profile-email" className={inputCls} />
          </Field>
          <Field icon={Phone} label="Phone">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(optional)" data-testid="affiliate-profile-phone" className={inputCls} />
          </Field>

          <button
            type="submit" disabled={savingProfile}
            data-testid="affiliate-profile-save"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-[#0A0E17] text-[13px] font-bold disabled:opacity-60"
          >
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
        </form>

        <form onSubmit={savePassword} className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-4" data-testid="affiliate-password-form">
          <h3 className="text-[14px] font-bold text-slate-100">Change password</h3>

          <Field icon={Lock} label="Current password">
            <input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="affiliate-current-password" className={inputCls} />
          </Field>
          <Field icon={KeyRound} label="New password">
            <input type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" data-testid="affiliate-new-password" className={inputCls} />
          </Field>
          <Field icon={KeyRound} label="Confirm new password">
            <input type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="affiliate-confirm-password" className={inputCls} />
          </Field>

          <button
            type="submit" disabled={savingPassword}
            data-testid="affiliate-password-save"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white/10 hover:bg-white/15 text-slate-100 text-[13px] font-bold disabled:opacity-60"
          >
            {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password
          </button>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full h-10 pl-9 pr-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/50";

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">{label}</label>
      <div className="relative">
        <Icon className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        {children}
      </div>
    </div>
  );
}
